import React, { ReactNode, useMemo, useState } from "react";
import {
  Box,
  Drawer,
  Typography,
  Avatar,
  Divider,
  Menu,
  MenuItem,
  ListItemIcon,
  IconButton,
} from "@mui/material";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import LogoutIcon from "@mui/icons-material/Logout";
import SettingsIcon from "@mui/icons-material/Settings";
import useSession from "../../hooks/useSession";
import { useUserInfo } from "../../hooks/useUserInfo";
import RenderMenuItem from "./RenderMenuItem";
import TopBar from "./TopBar";
import { getAllowedTableauReports } from "../../utils/tableauConfig";

const menuIcon = require("../../assets/menu-icon.png");
const analyticsIcon = require("../../assets/analytics.png");

interface SidebarProps {
  children: ReactNode;
}


const Sidebar: React.FC<SidebarProps> = ({ children }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const { user, handleLogout, rolesSet } = useSession();
  const { parsedTenantAttribute, tenant, loadingInfo, person } = useUserInfo();
  const { firstName, lastName } = person || {};

const tableauReports = useMemo(
  () => getAllowedTableauReports(parsedTenantAttribute, rolesSet),
  [parsedTenantAttribute, rolesSet]
);


  // Build menu items from tableau config
  const menuItems = useMemo(
    () =>
      tableauReports.map((r) => ({
        label: r.label,
        to: `/analytics/reports/${r.path}`,
      })),
    [tableauReports]
  );

  const handleClick = (e: React.MouseEvent<HTMLElement>) =>
    setAnchorEl(e.currentTarget);
  const handleClose = () => setAnchorEl(null);

  return (
    <Box
      sx={{
        display: "flex",
        height: "100vh",
        width: "100vw",
        overflowX: "hidden",
      }}
    >
      <Drawer
        variant="permanent"
        sx={{
          width: 292,
          flexShrink: 0,
          "& .MuiDrawer-paper": {
            width: 292,
            boxSizing: "border-box",
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            px: 2.5,
            py: 2,
          },
        }}
      >
        {/* Top */}
        <Box>
          {/* Brand Header */}
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            pt={2}
            pb={3}
          >
            <Box display="flex" alignItems="center" gap={1.5}>
              <Typography fontSize={15} fontWeight={600}>
                {"Client Portal"}
              </Typography>
            </Box>

            {/* <IconButton size="small">
              <Box
                component="img"
                src={menuIcon}
                alt="Menu"
                sx={{ width: 20, height: 20 }}
              />
            </IconButton> */}
          </Box>

          {/* Section Title */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              mb: 1.5,
              color: "#111",
            }}
          >
            <IconButton size="small" disableRipple>
              <Box
                component="img"
                src={analyticsIcon}
                alt="Analytics"
                sx={{ width: 20, height: 20 }}
              />
            </IconButton>
            <Typography fontSize={13} fontWeight={600}>
              Analytics
            </Typography>
          </Box>

          {/* Menu */}
          <Box sx={{ mt: 1 }}>
            {loadingInfo ? (
              <Typography fontSize={13} color="#777" sx={{ px: 1 }}>
                Loading reports…
              </Typography>
            ) : menuItems.length ? (
              menuItems.map((item) => (
                <RenderMenuItem key={item.label} item={item} />
              ))
            ) : (
              <Typography fontSize={13} color="#777" sx={{ px: 1 }}>
                No reports configured.
              </Typography>
            )}
          </Box>
        </Box>

        {/* Bottom user block */}
        <Box sx={{ pb: 1 }}>
          <Divider sx={{ my: 2 }} />

          <Box
            onClick={handleClick}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              cursor: "pointer",
              px: 1,
              py: 1,
              borderRadius: 2,
              "&:hover": { background: "#F6F6F6" },
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Avatar sx={{ width: 32, height: 32 }} src={user?.picture} />
              <Box sx={{ overflow: "hidden" }}>
                <Typography
                  fontSize={13}
                  fontWeight={600}
                  sx={{ whiteSpace: "nowrap", textOverflow: "ellipsis" }}
                >
                  {firstName || ""} {" "} {lastName || ""}
                </Typography>
                <Typography
                  fontSize={12}
                  color="#6F6F6F"
                  sx={{ whiteSpace: "nowrap", textOverflow: "ellipsis" }}
                >
                  {user?.email}
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: "flex", flexDirection: "column" }}>
              <ExpandLessIcon fontSize="small" />
              <ExpandMoreIcon fontSize="small" />
            </Box>
          </Box>

          <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
            {/* <MenuItem onClick={handleClose}>
              <ListItemIcon>
                <SettingsIcon fontSize="small" />
              </ListItemIcon>
              Account Settings
            </MenuItem>
            <Divider /> */}
            <MenuItem onClick={handleLogout}>
              <ListItemIcon>
                <LogoutIcon fontSize="small" />
              </ListItemIcon>
              Logout
            </MenuItem>
          </Menu>
        </Box>
      </Drawer>

      {/* Right side layout */}
      <Box
        sx={{
          flexGrow: 1,
          display: "flex",
          flexDirection: "column",
          minWidth: 0,
        }}
      >
        <Box sx={{ flexShrink: 0, position: "sticky", top: 0, zIndex: 1100 }}>
          <TopBar />
        </Box>

        <Box
          sx={{
            flexGrow: 1,
            overflowY: "auto",
            overflowX: "hidden",
            minWidth: 0,
            p: 2,
            bgcolor: "#FAFAFA",
          }}
        >
          {children}
        </Box>
      </Box>
    </Box>
  );
};

export default Sidebar;
